import { useState } from 'react';
import { Star, Play, Filter, ChevronLeft, ChevronRight, Quote, X } from 'lucide-react';
import { TESTIMONIALS } from '../data/mockData';
import { Dialog, DialogContent } from '../components/ui/dialog';

export const Results = () => {
  const [selectedGoal, setSelectedGoal] = useState('all');
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  const goals = [
    { id: 'all', name: 'Todos' },
    { id: 'weight', name: 'Pérdida de peso' },
    { id: 'muscle', name: 'Ganancia muscular' },
    { id: 'flexibility', name: 'Flexibilidad' },
  ];

  const filteredTestimonials = TESTIMONIALS.filter((t) => {
    if (selectedGoal === 'all') return true;
    if (selectedGoal === 'weight') return t.goal.includes('Pérdida');
    if (selectedGoal === 'muscle') return t.goal.includes('Ganancia');
    if (selectedGoal === 'flexibility') return t.goal.includes('Flexibilidad');
    return true;
  });

  const beforeAfterTestimonials = TESTIMONIALS.filter(t => t.beforeAfter);

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-8 bg-slate-50" data-testid="results-page">
      <div className="container-app">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">
            Resultados reales
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            Transformaciones que inspiran
          </h1>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            Historias reales de miembros que cambiaron su vida con FitCenter.
          </p>
        </div>

        {/* Before/After Section */}
        {beforeAfterTestimonials.length > 0 && (
          <section className="mb-16" data-testid="before-after-section">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
              Antes y después
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {beforeAfterTestimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="card-premium p-4 cursor-pointer"
                  onClick={() => setSelectedTestimonial(testimonial)}
                  data-testid={`before-after-${testimonial.id}`}
                >
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="relative">
                      <img
                        src={testimonial.beforeAfter.before}
                        alt="Antes"
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        Antes
                      </span>
                    </div>
                    <div className="relative">
                      <img
                        src={testimonial.beforeAfter.after}
                        alt="Después"
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <span className="absolute bottom-2 left-2 bg-[#16263B] text-white text-xs px-2 py-1 rounded-full font-medium">
                        Después
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.goal}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-4">
              *Los resultados varían según cada persona, dieta y constancia.
            </p>
          </section>
        )}

        {/* Testimonials Section */}
        <section data-testid="testimonials-list-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-2xl text-slate-900">
              Testimonios
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {goals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => setSelectedGoal(goal.id)}
                className={`filter-chip ${selectedGoal === goal.id ? 'active' : ''}`}
                data-testid={`filter-${goal.id}`}
              >
                {goal.name}
              </button>
            ))}
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredTestimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`testimonial-card animate-fade-in-up stagger-${(index % 4) + 1}`}
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <Quote className="w-8 h-8 text-[#16263B] mb-4" />
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}, {testimonial.age}</p>
                      <p className="text-sm text-slate-500">{testimonial.goal}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#16263B] text-[#16263B]" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTestimonials.length === 0 && (
            <div className="text-center py-12 bg-slate-50 rounded-2xl">
              <p className="text-slate-500">No hay testimonios con este filtro</p>
            </div>
          )}
        </section>

        {/* Video Section (Mock) */}
        <section className="mt-16" data-testid="video-section">
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
            Videos de nuestros miembros
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="relative aspect-video bg-slate-100 rounded-2xl overflow-hidden cursor-pointer group"
                onClick={() => setShowVideo(true)}
                data-testid={`video-thumbnail-${i}`}
              >
                <img
                  src={`https://images.pexels.com/photos/${i === 1 ? '3756042' : '6456141'}/pexels-photo-${i === 1 ? '3756042' : '6456141'}.jpeg?auto=compress&cs=tinysrgb&w=800`}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-slate-900 ml-1" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-semibold">Testimonio de {i === 1 ? 'María' : 'Roberto'}</p>
                  <p className="text-sm text-white/80">{i === 1 ? '3:45' : '2:30'} min</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Video Modal (Mock) */}
        <Dialog open={showVideo} onOpenChange={setShowVideo}>
          <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black">
            <div className="relative aspect-video">
              <div className="absolute inset-0 flex items-center justify-center text-white">
                <div className="text-center">
                  <Play className="w-16 h-16 mx-auto mb-4 text-[#16263B]" />
                  <p className="text-lg">Video demo</p>
                  <p className="text-sm text-white/60">Este es un placeholder para el video</p>
                </div>
              </div>
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={!!selectedTestimonial} onOpenChange={(open) => !open && setSelectedTestimonial(null)}>
          <DialogContent className="max-w-lg p-0 overflow-hidden">
            {selectedTestimonial && (
              <div className="p-6">
                {selectedTestimonial.beforeAfter && (
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="relative">
                      <img
                        src={selectedTestimonial.beforeAfter.before}
                        alt="Antes"
                        className="w-full h-64 object-cover rounded-xl"
                      />
                      <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        Antes
                      </span>
                    </div>
                    <div className="relative">
                      <img
                        src={selectedTestimonial.beforeAfter.after}
                        alt="Después"
                        className="w-full h-64 object-cover rounded-xl"
                      />
                      <span className="absolute bottom-2 left-2 bg-[#16263B] text-white text-xs px-2 py-1 rounded-full font-medium">
                        Después
                      </span>
                    </div>
                  </div>
                )}
                <Quote className="w-8 h-8 text-[#16263B] mb-4" />
                <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                  "{selectedTestimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={selectedTestimonial.image}
                    alt={selectedTestimonial.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{selectedTestimonial.name}, {selectedTestimonial.age}</p>
                    <p className="text-sm text-slate-500">{selectedTestimonial.goal}</p>
                    <div className="flex items-center gap-0.5 mt-1">
                      {[...Array(selectedTestimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#16263B] text-[#16263B]" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-6">
                  *Los resultados varían según cada persona, dieta y constancia.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
